import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import {
  API_get_logbook_columns,
  API_get_logbook_entries_report,
} from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportCsvDownloadButton from "../ReportCsvDownloadButton";
import ReportEmailToggle from "../ReportEmailToggle";

// Default range: last 7 days.
function defaultDateRange() {
  const end = dayjs().startOf("day");
  const start = end.subtract(6, "day");
  return { start, end };
}

function formatValue(entry) {
  // Categories are mutable; an entry formats under the type it was RECORDED
  // with (the category's current type covers pre-backfill rows). Types are
  // stored lowercase ("star", "dollar", "percentage").
  const type = String(entry?.recorded_type || entry?.category?.type || "").toLowerCase();
  const text = entry?.value_text;
  const integer = entry?.value_integer;
  const float = entry?.value_float;

  // The logbook saves star ratings in value_float (older rows may use
  // value_integer).
  const stars = float ?? integer;
  if (type === "star" && stars != null) {
    const n = Math.max(0, Math.min(5, Math.round(Number(stars))));
    return "★".repeat(n) + "☆".repeat(5 - n);
  }
  if (type === "dollar" && float != null) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(float));
  }
  // Percentages are entered and stored in points (32.5 means 32.5%), as the
  // logbook shows them.
  if (type === "percentage" && float != null) {
    return `${Number(float)}%`;
  }
  if (text) return text;
  if (integer != null) return String(integer);
  if (float != null) return String(float);
  return "";
}

export default function LogEntriesByTypeReport({
  report,
  onBack,
  storePicker,
  homeStore,
  homeCompany,
  userId,
}) {
  const initial = useMemo(defaultDateRange, []);
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [searchQuery, setSearchQuery] = useState("");

  // Categories ("types") to filter by. Loaded on mount; default = all.
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryUuids, setSelectedCategoryUuids] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Entries (the actual report content).
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Load available categories (the "types") once on mount. We mirror the
  // existing LogbookReportingDisplay: pull both columns, flatten their
  // categories, default to all-selected.
  useEffect(() => {
    if (!homeCompany) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await getRightworkAPI(API_get_logbook_columns, {
          company_id: homeCompany,
        });
        if (cancelled) return;
        const columns = res?.data?.redux?.payload?.logbook_columns || [];
        const flat = [];
        for (const col of columns) {
          const colName = "Logbook";
          for (const cat of col.categories || []) {
            flat.push({
              uuid: cat.uuid,
              name: cat.name,
              type: cat.type,
              columnName: colName,
            });
          }
        }
        flat.sort((a, b) => a.name.localeCompare(b.name));
        setAllCategories(flat);
        setSelectedCategoryUuids(flat.map((c) => c.uuid));
        setCategoriesLoaded(true);
      } catch (e) {
        if (cancelled) return;
        // Soft-fail — leave categories empty. The entries fetch will still
        // run and return everything for the date range.
        setAllCategories([]);
        setCategoriesLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeCompany]);

  // Fetch entries when filters change. Skipped until categories are loaded
  // so we don't fire a fetch with an empty category list and accidentally
  // filter to nothing.
  useEffect(() => {
    if (!homeStore || !categoriesLoaded) return undefined;
    if (endDate.isBefore(startDate, "day")) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const params = {
          // The endpoint takes the plural `store_ids` for the actual query,
          // but the permission helper looks for `store_id` (singular) to do
          // the auth check. Send both — mirrors the existing LogbookReporting
          // display.
          store_id: homeStore,
          store_ids: String(homeStore),
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        };
        // Only send category_uuids when the user has narrowed down. Empty
        // selection = show nothing. All-selected = omit filter to keep the
        // URL short and let the server return everything.
        if (
          selectedCategoryUuids.length > 0 &&
          selectedCategoryUuids.length < allCategories.length
        ) {
          params.category_uuids = selectedCategoryUuids.join(",");
        } else if (selectedCategoryUuids.length === 0 && allCategories.length > 0) {
          // User explicitly cleared selection — return zero rows.
          setEntries([]);
          setSummary(null);
          setIsLoading(false);
          return;
        }
        if (searchQuery.trim()) {
          params.search_query = searchQuery.trim();
        }

        const res = await getRightworkAPI(
          API_get_logbook_entries_report,
          params
        );
        if (cancelled) return;
        const payload = res?.data?.redux?.payload || {};
        setEntries(Array.isArray(payload.entries) ? payload.entries : []);
        setSummary(payload.summary || null);
      } catch (e) {
        if (cancelled) return;
        setError(
          "Couldn't load log entries. Try again or adjust the filters."
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    homeStore,
    startDate,
    endDate,
    selectedCategoryUuids,
    searchQuery,
    categoriesLoaded,
    allCategories.length,
    retryNonce,
  ]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  const handleCategoryChange = useCallback(
    (event) => {
      const value = event.target.value;
      // MUI multiselect returns an array. If the user clicked "All", toggle.
      if (Array.isArray(value) && value.includes("__all__")) {
        if (selectedCategoryUuids.length === allCategories.length) {
          setSelectedCategoryUuids([]);
        } else {
          setSelectedCategoryUuids(allCategories.map((c) => c.uuid));
        }
        return;
      }
      setSelectedCategoryUuids(Array.isArray(value) ? value : []);
    },
    [selectedCategoryUuids, allCategories]
  );

  const renderCategorySelection = useCallback(
    (selected) => {
      if (!Array.isArray(selected) || selected.length === 0) {
        return "No types selected";
      }
      if (selected.length === allCategories.length) return "All types";
      if (selected.length === 1) {
        const cat = allCategories.find((c) => c.uuid === selected[0]);
        return cat?.name || "1 type";
      }
      return `${selected.length} types`;
    },
    [allCategories]
  );

  const datePickerSx = {
    "& .MuiOutlinedInput-root": {
      height: "36px",
      backgroundColor: "white",
      borderRadius: "6px",
      fontSize: "13px",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6d28d9",
      },
    },
  };

  const filters = (
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <div className="text-xs text-slate-500 mb-1">Start date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={startDate}
            onChange={(v) => v && setStartDate(v.startOf("day"))}
            slotProps={{
              day: {
                sx: {
                  "&.MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#6d28d9",
                  },
                },
              },
            }}
            sx={datePickerSx}
          />
        </LocalizationProvider>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">End date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={endDate}
            onChange={(v) => v && setEndDate(v.startOf("day"))}
            slotProps={{
              day: {
                sx: {
                  "&.MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#6d28d9",
                  },
                },
              },
            }}
            sx={datePickerSx}
          />
        </LocalizationProvider>
      </div>
      <div className="min-w-[180px]">
        <div className="text-xs text-slate-500 mb-1">Type</div>
        <Select
          multiple
          value={selectedCategoryUuids}
          onChange={handleCategoryChange}
          input={
            <OutlinedInput
              size="small"
              sx={{
                height: "36px",
                backgroundColor: "white",
                fontSize: "13px",
                borderRadius: "6px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            />
          }
          renderValue={renderCategorySelection}
          size="small"
        >
          <MenuItem value="__all__" dense>
            <Checkbox
              checked={
                allCategories.length > 0 &&
                selectedCategoryUuids.length === allCategories.length
              }
              indeterminate={
                selectedCategoryUuids.length > 0 &&
                selectedCategoryUuids.length < allCategories.length
              }
              sx={{ "&.Mui-checked": { color: "#6d28d9" } }}
            />
            <ListItemText primary="All types" />
          </MenuItem>
          {allCategories.map((cat) => (
            <MenuItem key={cat.uuid} value={cat.uuid} dense>
              <Checkbox
                checked={selectedCategoryUuids.includes(cat.uuid)}
                sx={{ "&.Mui-checked": { color: "#6d28d9" } }}
              />
              <ListItemText primary={cat.name} secondary={cat.columnName} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">Search</div>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter by text…"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "36px",
              backgroundColor: "white",
              fontSize: "13px",
              borderRadius: "6px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6d28d9",
              },
            },
            minWidth: "200px",
          }}
        />
      </div>
    </div>
  );

  // The CSV download sends the page's filters so it matches the table;
  // with every type deselected the page shows nothing, so neither does it.
  const logEntriesCsvParams =
    allCategories.length > 0 && selectedCategoryUuids.length === 0
      ? null
      : {
          ...(selectedCategoryUuids.length > 0 &&
          selectedCategoryUuids.length < allCategories.length
            ? { category_uuids: selectedCategoryUuids.join(",") }
            : {}),
          ...(searchQuery.trim() ? { search_query: searchQuery.trim() } : {}),
        };

  return (
    <PrebuiltReportShell
      actions={
        <>
          <ReportEmailToggle
            reportId={report.id}
            userId={userId}
            schedule={report.emailSchedule}
          />
          <ReportCsvDownloadButton
            reportId="log-entries-by-type"
            storeId={homeStore}
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
            extraParams={logEntriesCsvParams}
            disabled={!logEntriesCsvParams}
          />
        </>
      }
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No logbook entries match these filters.
        </div>
      ) : (
        <>
          {summary && (
            <div className="mb-3 text-xs text-slate-500">
              {summary.total_entries} entr
              {summary.total_entries === 1 ? "y" : "ies"} · {summary.date_range}
              {summary.categories_included
                ? ` · ${summary.categories_included} type${
                    summary.categories_included === 1 ? "" : "s"
                  }`
                : ""}
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-slate-700">
                  <th className="text-left font-semibold px-3 py-2 w-[110px]">
                    Date
                  </th>
                  <th className="text-left font-semibold px-3 py-2 w-[180px]">
                    Type
                  </th>
                  <th className="text-left font-semibold px-3 py-2 w-[160px]">
                    Author
                  </th>
                  <th className="text-left font-semibold px-3 py-2">Entry</th>
                  <th className="text-left font-semibold px-3 py-2 w-[90px]">
                    Files
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.uuid}
                    className="border-b border-slate-100 hover:bg-slate-50 align-top"
                  >
                    <td className="px-3 py-2 text-slate-700">{e.date}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {e.category?.name || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {e.author?.full_name || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-pre-wrap">
                      {formatValue(e)}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {(e.file_uploads || []).length > 0
                        ? `${e.file_uploads.length} file${
                            e.file_uploads.length === 1 ? "" : "s"
                          }`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
